/**
 * BleedTable — the one table, bleeding to its card edge.
 *
 * WHY THIS EXISTS. Design gate D3: "Cards, tables and lists on different screens
 * share one component each. Two visually different tables in one product is a
 * fail." The product had a shared table class and no shared table component, so
 * every screen hand-rolled its head row and picked its own alignment. Column
 * alignment came up repeatedly in the 15 September design review, and it is the
 * kind of thing a component can simply make impossible to get wrong.
 *
 * THE TWO RULES IT ENFORCES.
 *
 * 1. Every header is LEFT-aligned and sits directly above the first character
 *    of its cell, numeric columns included. Right-aligned numbers under a
 *    left-aligned header, or a right-aligned header over left-aligned chips,
 *    both read as broken. A caller cannot set a header alignment: it is decided
 *    here from the column kind, so the header and the cells cannot disagree.
 * 2. The trailing action column is the only right-aligned one, and its header
 *    is an invisible spacer, so the column contributes width without appearing
 *    to be a heading for a column of buttons.
 *
 * The table bleeds out of its card by the card padding on both sides, so its
 * rules read full-width while the first column still lines up with everything
 * else in the card. Geometry lives in CSS off --card-pad, so a retune of the
 * card padding carries here with no second number to remember.
 *
 * Contract: docs/redesign-20260915/design_handoff_app_cohesion/README.md,
 * "Tables". Styling: styles/design-system.css, .bleed-table (unscoped).
 */

import { Fragment, type ReactNode } from 'react';

/**
 * What kind of column this is. It decides alignment, numerals and whether the
 * header has a visible label, and it is the ONLY lever a caller gets.
 *   text   the default: left, proportional figures
 *   num    left header and left cells, TABULAR figures so digits stack
 *   action the trailing button column: right-aligned, invisible header label
 */
export type ColumnKind = 'text' | 'num' | 'action';

export interface BleedColumn {
  /** Stable key, used for the React key and the cell lookup. */
  key: string;
  /** The header label. Rendered but visually hidden on an action column. */
  header: string;
  kind?: ColumnKind;
}

/** Severity on a row, drawn as the same 2px left edge used everywhere else. */
export type RowTone = 'overdue' | 'soon' | 'routine';

export interface BleedRow {
  /** Stable key for this row. */
  key: string;
  tone?: RowTone;
  /**
   * `data-testid` on the `<tr>`. Optional and additive: a screen with one row
   * that means something on its own (the Client Book's "Preparing" placeholder)
   * has to be able to name it, and a Playwright spec cannot reach a row by
   * React key.
   */
  testId?: string;
  /**
   * A GROUP HEADING INSIDE THE BODY. Optional and additive (cohesion pass
   * phase 5, 2026-09-15). When set, this row renders as one `<th
   * scope="rowgroup">` spanning every column and `cells` is ignored; absent
   * means no such row is emitted at all, so a flat table is byte-identical to
   * what it was.
   *
   * WHY A TABLE NEEDS ONE. The trust portfolio register is grouped by asset
   * class, and the trust audit trail is grouped by day. Both were hand-rolled
   * tables with a `colSpan` heading row because the shared component could not
   * express a group, which is exactly the pressure that grows a second table in
   * the product. A group is a heading, not a row of data: `scope="rowgroup"`
   * says so to a screen reader, where a `<td colSpan>` says nothing.
   */
  group?: ReactNode;
  /** Cell content by column key. A missing key renders an empty cell. */
  cells: Record<string, ReactNode>;
  /**
   * THE ROW DETAIL. Optional and additive (cohesion pass phase 4, 2026-09-15).
   * Rendered as a second `<tr>` directly under this one, spanning every
   * column, when the caller decides this row is open. Absent means no extra
   * row is emitted at all, so a table that never expands is byte-identical to
   * what it was.
   *
   * WHY A TABLE NEEDS ONE. Paul's ruling 4 of 15 September: `Pay out` lives
   * inside the row detail only, never on a list row. That is a rule about
   * money, not about layout, and it needs somewhere to live that is part of
   * the row rather than a modal that loses the row's context. Anything a
   * reader must open the row to see belongs here: the governance controls, the
   * authorising resolution picker, the treasury actions.
   */
  detail?: ReactNode;
}

export interface BleedTableProps {
  columns: BleedColumn[];
  rows: BleedRow[];
  /** Accessible name for the table. Required: a table with no caption is a
   *  wall of numbers to a screen reader. */
  label: string;
  /** The footer line under the table: a count, a pager, a link to the rest. */
  footer?: ReactNode;
  testId?: string;
}

const CELL_CLASS: Record<ColumnKind, string> = {
  text: '',
  num: 'is-num',
  action: 'is-action',
};

const ROW_CLASS: Record<RowTone, string> = {
  overdue: 'is-overdue',
  soon: 'is-soon',
  routine: '',
};

export function BleedTable({ columns, rows, label, footer, testId }: BleedTableProps) {
  return (
    <>
      <div className="bleed-table-wrap">
        <table
          className="bleed-table"
          aria-label={label}
          data-testid={testId ? `bleed-table-${testId}` : 'bleed-table'}
        >
          <thead>
            <tr>
              {columns.map((c) => {
                const kind = c.kind ?? 'text';
                const cls = CELL_CLASS[kind];
                return (
                  <th key={c.key} scope="col" {...(cls ? { className: cls } : {})}>
                    {/* The action column's label is present for assistive tech
                        and invisible on screen (opacity, not display:none, so
                        it still contributes the column's width). */}
                    {kind === 'action' ? <span>{c.header}</span> : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              /* A group heading is a heading, so it is emitted before any of
                 the cell logic below and carries none of it: no tone marker, no
                 action column, no detail. */
              if (r.group !== undefined) {
                return (
                  <tr
                    key={r.key}
                    className="bleed-table-group"
                    {...(r.testId ? { 'data-testid': r.testId } : {})}
                  >
                    <th scope="rowgroup" colSpan={columns.length}>
                      {r.group}
                    </th>
                  </tr>
                );
              }
              const rowCls = ROW_CLASS[r.tone ?? 'routine'];
              const row = (
                <tr
                  key={r.key}
                  {...(rowCls ? { className: rowCls } : {})}
                  {...(r.testId ? { 'data-testid': r.testId } : {})}
                >
                  {columns.map((c) => {
                    const cls = CELL_CLASS[c.kind ?? 'text'];
                    return (
                      <td key={c.key} {...(cls ? { className: cls } : {})}>
                        {r.cells[c.key] ?? null}
                      </td>
                    );
                  })}
                </tr>
              );
              /* The detail row, when the caller says this row is open. One
               * cell spanning the table, so the columns above it do not
               * reflow while it is open — a detail that moves the header is
               * a detail nobody trusts. */
              const detailRow = r.detail ? (
                <tr key={`${r.key}-detail`} className="bleed-table-detail-row">
                  <td colSpan={columns.length}>
                    <div className="bleed-table-detail">{r.detail}</div>
                  </td>
                </tr>
              ) : null;
              return detailRow ? (
                <Fragment key={`${r.key}-group`}>
                  {row}
                  {detailRow}
                </Fragment>
              ) : (
                row
              );
            })}
          </tbody>
        </table>
      </div>
      {footer ? <div className="bleed-table-foot">{footer}</div> : null}
    </>
  );
}
